<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col cols="12" xl="6">
        <b-card :header="$t('Lottery Event')" style="position:relative">
          <b-form @submit.prevent="onSubmitDetails" autocomplete="off" ref="evpanel">
            <b-row>
              <b-col cols="12" md="6">
                <FloatingLabel
                  v-model="details.system_name"
                  label="System Name"
                  type="plain"
                  size="sm"
                />
              </b-col>
              <b-col cols="12" md="6">
                <FloatingLabel v-model="details.code" label="Code" size="sm" />
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="12" md="6">
                <b-form-group class="mb-3">
                  <date-range-picker
                    :startDate="start1"
                    :endDate="start1"
                    @update="updateScheduleTime"
                    :locale-data="locale"
                    opens="right"
                    :timePicker="true"
                    :timePicker24Hour="true"
                    :timePickerIncrement="1"
                    :timePickerSeconds="true"
                    :ranges="false"
                    :singleDatePicker="true"
                    :label="$t('Schedule Time')"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group class="mb-3">
                  <date-range-picker
                    :startDate="start2"
                    :endDate="start2"
                    @update="updateActualTime"
                    :locale-data="locale"
                    opens="center"
                    :timePicker="true"
                    :timePicker24Hour="true"
                    :timePickerIncrement="1"
                    :timePickerSeconds="true"
                    :ranges="false"
                    :singleDatePicker="true"
                    :label="$t('Actual Time')"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="12" md="6">
                <b-form-group :label="$t('Offered')" class="grouping py-2 px-3 mb-3 mt-0">
                  <b-form-checkbox
                    v-model="details.offered"
                    :value="true"
                    :unchecked-value="false"
                    disabled
                  >{{$t('Appear in offer list')}}</b-form-checkbox>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="3">
                <b-form-group :label="$t('Blocked')" class="grouping py-2 px-3 mb-3 mt-0">
                  <b-form-checkbox
                    v-model="details.blocked"
                    :value="true"
                    :unchecked-value="false"
                  >{{$t('Event blocked')}}</b-form-checkbox>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="3">
                <b-form-group :label="$t('Canceled')" class="grouping py-2 px-3 mb-3 mt-0">
                  <b-form-checkbox
                    v-model="details.canceled"
                    :value="true"
                    :unchecked-value="false"
                  >{{$t('Event canceled')}}</b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="12" md="6">
                <FloatingLabel
                  v-model="details.automatic_processing_max_amount"
                  label="Automatic Processing Max Amount"
                  size="sm"
                />
              </b-col>
              <b-col cols="12" md="6">
                <FloatingLabel
                  v-model="details.automatic_processing_max_ratio"
                  label="Automatic Processing Max Ratio"
                  size="sm"
                />
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="12" md="6">
                <b-form-group
                  :label="$t('Automatic Processing')"
                  class="grouping py-2 px-3 mb-3 mt-0"
                >
                  <b-form-checkbox
                    v-model="details.automatic_processing"
                    :value="true"
                    :unchecked-value="false"
                  >{{$t('Enable')}}</b-form-checkbox>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group :label="$t('Processed')" class="grouping py-2 px-3 mb-3 mt-0">
                  <b-form-checkbox
                    v-model="details.processed"
                    :value="true"
                    :unchecked-value="false"
                    disabled
                  >{{$t('Is processed')}}</b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>

            <FloatingLabel
              v-model.number="details.bet_block_offset"
              label="Bet Block Offset"
              description="Block bets with X seconds before lottery event starts"
              size="sm"
            />

            <b-form-group
              :label="$t('Manual Processing Alert Emails')"
              class="grouping p-3 mb-3 mt-0"
            >
              <ListEditor
                :items="details.manual_processing_alert_emails"
                :addLabel="$t('Add new email address')"
                :deleteTootltip="$t('Delete')"
                size="sm"
              />
            </b-form-group>

            <b-form-group
              :label="$t('Odds')"
              class="grouping p-3 mb-3 mt-0"
              :description="$t('Odds for X/M (X = 1 .. M)')"
            >
              <ListEditor
                :items="details.odds"
                :addLabel="$t('Add new odd')"
                :deleteTootltip="$t('Delete')"
                :showIndex="true"
                :m="m"
                :required="true"
                size="sm"
                :flex="true"
                flexWidth="140px"
              />
            </b-form-group>

            <b-form-group
              class="grouping p-3 mb-3 mt-0"
              label="Custom Odds"
              :description="$t('Custom odds for certain combinations')"
            >
              <RegexListEditor
                :items="details.custom_odds"
                :addLabel="$t('Add new custom odd')"
                :deleteTootltip="$t('Delete')"
                placeholderRegex="wildcard"
                placeholderValue="value"
                size="sm"
                :flex="true"
                flexWidth="200px"
              />
            </b-form-group>

            <b-form-group :label="$t('Max Liability')" class="grouping p-3 mb-3 mt-0">
              <ListEditor
                :items="details.max_liability"
                :addLabel="$t('Add new liability')"
                :deleteTootltip="$t('Delete')"
                :showIndex="true"
                :m="m"
                :required="true"
                size="sm"
                :flex="true"
                flexWidth="140px"
              />
            </b-form-group>

            <b-form-group
              :label="$t('Custom Liability')"
              class="grouping p-3 mb-3 mt-0"
              :description="$t('Custom liability for any combination')"
            >
              <RegexListEditor
                :items="details.custom_liability"
                :addLabel="$t('Add new custom liability')"
                :deleteTootltip="$t('Delete')"
                placeholderRegex="wildcard"
                placeholderValue="value"
                size="sm"
                :flex="true"
                flexWidth="200px"
              />
            </b-form-group>

            <div style="text-align:right">
              <b-button type="submit" variant="success">{{$t('Save')}}</b-button>
            </div>
          </b-form>
        </b-card>
      </b-col>
      <b-col cols="12" xl="6">
        <b-card :header="$t('Results')" style="position:relative">
          <b-row ref="respanel">
            <b-col cols="6">
              <FloatingLabel
                v-model="details.results_created"
                label="Results Created"
                size="sm"
                type="plain"
                :readonly="true"
              />
            </b-col>
            <b-col cols="6">
              <FloatingLabel
                v-model="details.results_modified"
                label="Results Modifed"
                size="sm"
                type="plain"
                :readonly="true"
              />
            </b-col>
          </b-row>

          <b-form-group :label="$t('Results')" class="grouping p-3 mb-3 mt-0">
            <div v-if="details.results && details.results.length">
              <div>
                <ListEditor
                  :items="details.results"
                  :showIndex="true"
                  prefix="#"
                  :max="k"
                  size="sm"
                  :required="true"
                  :disabled="true"
                  :flex="true"
                  flexWidth="90px"
                />
              </div>
              <div>
                <b-button
                  type="button"
                  variant="danger"
                  size="sm"
                  class="mt-2"
                  @click.prevent="deleteResults"
                >{{$t('Delete')}}</b-button>
                <b-button
                  type="button"
                  variant="brand"
                  size="sm"
                  class="mt-2 ml-2"
                  v-if="!details.processed"
                  @click.prevent="processResults"
                >{{$t('Process')}}</b-button>
              </div>
            </div>
            <div v-else>
              <b-button
                type="button"
                variant="success"
                size="sm"
                @click.prevent="addResults"
              >{{$t('Add new result set')}}</b-button>
              <b-button
                type="button"
                variant="success"
                size="sm"
                class="ml-2"
                v-if="!details.processed && !details.canceled"
                @click.prevent="cancelEvent"
              >{{$t('Cancel Event')}}</b-button>
            </div>
            <div
              v-if="process_results && typeof process_results.tickets_lost != 'undefined'"
              class="text-dark my-2"
            >
              <div class="mt-4 mb-2">
                {{$t('Total Tickets')}}:
                <strong>
                  {{process_results.total_tickets}}
                  {{$t("tickets")}}
                </strong>
                |
                {{$t('Lost')}}:
                <strong>
                  {{process_results.tickets_lost}}
                  {{$t("tickets")}}
                </strong>
                |
                {{$t('Won')}}:
                <strong>
                  {{process_results.tickets_won}}
                  {{$t("tickets")}}
                </strong>
                |
                {{$t('Stake')}}:
                <strong>{{process_results.total_stake}}</strong>
                |
                {{$t('Stake Lost')}}:
                <strong>{{process_results.total_stake_lost}}</strong>
                |
                {{$t('Winning Amount')}}:
                <strong>{{process_results.total_win_amount}}</strong>
                |
                {{$t('Winning Tax')}}:
                <strong>{{process_results.total_win_tax}}</strong>
              </div>
              <div>
                <router-link :to="`/tickets?lid=${eid}`">{{$t("See Tickets")}}</router-link>
              </div>
            </div>
          </b-form-group>
        </b-card>
        <b-card :header="$t('Liability Counters')" style="position:relative">
          <LiabilityCounters :event_id="eid" />
        </b-card>
        <b-card :header="$t('Entity Log')" style="position:relative">
          <EntityLog :event_id="eid" />
        </b-card>
      </b-col>
    </b-row>
    <b-modal id="bv-modal-results" size="lg" hide-footer centered>
      <template slot="modal-title">{{$t('Results Set')}}</template>
      <template slot="modal-header-close">
        <span />
      </template>
      <div class="d-block" style="position:relative">
        <div v-if="!resultSetRes">
          <div class="mb-3 mt-0 mx-1 text-black">{{$t("Paste here (comma delimited numbers) ...")}}</div>
          <FloatingLabel
            v-model="resultSetString"
            label="Results as comma delimited numbers"
            size="sm"
            type="textarea"
            style="margin-bottom: 0.5rem !important;"
            :state="resultSetStringState"
            aria-describedby="input-live-feedback"
          >
            <template v-slot:feedback>
              <div
                id="input-live-feedback"
                class="invalid-feedback"
              >{{$t('One or more numbers are invalid')}}</div>
            </template>
          </FloatingLabel>
          <div class="mb-3 text-black">{{$t("... or type in your result numbers bellow.")}}</div>
        </div>
        <b-form @submit.prevent="onSaveResults" autocomplete="off" ref="l">
          <b-form-group v-if="!resultSetRes" :label="$t('Results')" class="grouping p-3 mb-3 mt-0">
            <ResultsEditor
              :items="resultSet"
              :max="r"
              size="sm"
              :flex="true"
              flexWidth="90px"
              :showIndex="true"
              prefix="#"
            />
          </b-form-group>
          <b-form-group
            v-if="!resultSetRes"
            :label="$t('Process')"
            class="grouping py-2 px-3 mb-3 mt-0"
          >
            <b-form-checkbox
              v-model="process"
              :value="true"
              :unchecked-value="false"
            >{{$t('Process event')}}</b-form-checkbox>
          </b-form-group>
          <div class="text-right">
            <b-button
              type="submit"
              variant="success"
              class="mt-3"
              v-if="!resultSetRes"
            >{{$t('Save')}}</b-button>
          </div>
          <div v-if="resultSetRes" class="text-dark">
            <div>
              {{$t('Total Tickets')}}:
              <strong>
                {{resultSetRes.total_tickets}}
                {{$t("tickets")}}
              </strong>
            </div>
            <div class="mb-2">
              {{$t('Lost')}}:
              <strong>
                {{resultSetRes.tickets_lost}}
                {{$t("tickets")}}
              </strong>
            </div>
            <div class="mb-2">
              {{$t('Won')}}:
              <strong>
                {{resultSetRes.tickets_won}}
                {{$t("tickets")}}
              </strong>
            </div>
            <div class="mb-2">
              {{$t('Stake')}}:
              <strong>{{resultSetRes.total_stake}}</strong>
            </div>
            <div class="mb-2">
              {{$t('Stake Lost')}}:
              <strong>{{resultSetRes.total_stake_lost}}</strong>
            </div>
            <div class="mb-2">
              {{$t('Winning Amount')}}:
              <strong>{{resultSetRes.total_win_amount}}</strong>
            </div>
            <div class="mb-2">
              {{$t('Winning Tax')}}:
              <strong>{{resultSetRes.total_win_tax}}</strong>
            </div>
            <div>
              <router-link :to="`/tickets?lid=${eid}`">{{$t("See Tickets")}}</router-link>
            </div>
          </div>
        </b-form>
      </div>
    </b-modal>
  </div>
</template>

<script>
import ListEditor from "@/components/ListEditor";
import RegexListEditor from "@/components/RegexListEditor";
import FloatingLabel from "@/components/FloatingLabel";
import ResultsEditor from "@/components/ResultsEditor";
import DateRangePicker from "@/components/calendar-range/DateRangePicker";
import { successConf, errorConf } from "@/shared/notifications";
import LiabilityCounters from "./components/LiabilityCounters";
import EntityLog from "./components/EntityLog";
import moment from "moment";
import timeFormat from "@/shared/timeFormat";

export default {
  name: "lotteryEvent",
  props: ["selectedeid"],
  components: {
    ListEditor,
    RegexListEditor,
    DateRangePicker,
    FloatingLabel,
    ResultsEditor,
    LiabilityCounters,
    EntityLog
  },
  data: function() {
    return {
      eid: "",
      details: {
        code: "", // integer	Pool of numbers starting with 100
        system_name: "", // text	Parent lottery system name
        schedule_time: "", // bigint	Scheduled event start time
        actual_time: "", // bigint	Actual event start time
        offered: "", // boolean NULL [false]	Appear in offer list (cannot be unset)
        blocked: "", // boolean NULL [false]	Event blocked
        canceled: "", // boolean NULL [false]	Event canceled
        bet_block_offset: null,
        automatic_processing_max_amount: "", // bigint NULL	Automatic processing up to amount (inherited)
        automatic_processing_max_ratio: "", // bigint NULL	Automatic processing up to ratio (inherited)
        automatic_processing: false,
        manual_processing_alert_emails: [], // text[] NULL	Alert email recipients (inherited)
        processed: false,
        odds: [], // bigint[]	Odds for X/K (X = 1 .. M) (inherited)
        custom_odds: [], // json NULL	Custom odds for certain combinations
        results_created: "", // bigint NULL	First results time
        results_modified: "", // bigint NULL	Last results time
        results: [], // integer[] NULL	Lottery results
        max_liability: [], // bigint[] NULL	Maximum liability for X/K (X = 1 .. M) (inherited)
        custom_liability: [] // json NULL	Custom liability for any combination
      },
      k: null,
      n: null,
      m: null,
      r: null,
      newId: false,
      start1: moment(),
      start2: moment(),
      locale: {
        format: timeFormat.datePickerLong, //format of the dates displayed
        firstDay: 1, //ISO first day of week - see moment documenations for details
        showWeekNumbers: true //show week numbers on each row of the calendar
      },

      resultSetString: "",
      resultSetStringState: null,
      resultSet: [],
      resultSetRes: null,
      process: false,

      process_results: null
    };
  },
  watch: {
    resultSetString(newVal, oldVal) {
      const self = this;
      self.resultSetStringState = null;
      if (newVal != oldVal && newVal) {
        // split by comma and remove empty elements
        const parts = newVal.split(",").filter(function(el) {
          return el.length != 0;
        });

        // trim whitespaces
        parts.forEach((item, i) => {
          parts[i] = parseInt(item.trim()).toString();
        });

        if (parts.length <= self.r) {
          let valid = true;

          // check to see if the elements ar enumbers and between allowed limits 1..n
          parts.forEach((item, i) => {
            if (isNaN(item)) {
              valid = false;
            } else {
              if (!(parseInt(item) > 0 && parseInt(item) <= self.n)) {
                valid = false;
              }
            }
          });

          // check to see if they're unique
          if (new Set(parts).size !== parts.length) {
            valid = false;
          }

          if (valid) {
            self.resultSet = parts;
          } else {
            self.resultSetStringState = false;
          }
        }
      }
    }
  },
  mounted() {
    this.eid = this.$route.params.id != "0" ? this.$route.params.id : "";

    if (this.selectedeid) {
      this.eid = this.selectedeid;
    } else if (!this.eid) {
      this.eid = "";
    }

    if (this.eid != "0" && this.eid != "") {
      this.loadData();
    } else {
      this.newId = true;
    }
    this.resetResultSet();
    this.$root.$on("bv::modal::hide", (bvEvent, modalId) => {
      if (modalId == "bv-modal-results") {
        this.resetResultSet();
      }
    });
  },
  destroyed() {
    this.$root.$off("bv::modal::hide");
  },
  methods: {
    cancelEvent() {
      this.details.canceled = true;
      this.onSubmitDetails();
    },
    resetResultSet() {
      const tmp = [];
      for (let i = 0; i < this.r; i++) {
        tmp.push(null);
      }
      this.resultSet = tmp;
      this.resultSetString = "";
      this.resultSetStringState = null;
      this.process = false;
      this.resultSetRes = null;
    },
    loadData() {
      if (this.eid == "") return;

      const loadDet = this.$loading.open({
        container: this.$refs.evpanel
      });

      this.$store
        .dispatch("lotteryEvent/read", this.eid)
        .then(resp => {
          this.populateData(resp.data);
        })
        .catch(err => {
          this.showErrorNotification(err);
        })
        .finally(() => {
          loadDet.close();
        });
    },
    onSubmitDetails() {
      const loader = this.$loading.open({
        container: this.$refs.evpanel
      });

      const d = JSON.parse(JSON.stringify(this.details));
      const detailsData = {};
      let apiAction = "lotteryEvent/update";
      if (this.newId) {
        apiAction = "lotteryEvent/create";
      } else {
        detailsData.eid = this.eid;
      }

      detailsData.code = parseInt(d.code);
      detailsData.system_name = d.system_name;
      detailsData.schedule_time = d.schedule_time;
      detailsData.actual_time = d.actual_time;
      detailsData.blocked = d.blocked;
      detailsData.canceled = d.canceled;
      detailsData.automatic_processing = d.automatic_processing;
      detailsData.processed = d.processed;
      detailsData.bet_block_offset = d.bet_block_offset;
      detailsData.automatic_processing_max_amount = parseInt(
        d.automatic_processing_max_amount
      );

      detailsData.automatic_processing_max_ratio = parseInt(
        d.automatic_processing_max_ratio
      );
      detailsData.manual_processing_alert_emails = d.manual_processing_alert_emails
        ? d.manual_processing_alert_emails
        : [];

      detailsData.odds = d.odds ? d.odds : [];
      for (let i = 0; i < detailsData.odds.length; i++) {
        detailsData.odds[i] = parseFloat(detailsData.odds[i]);
      }

      detailsData.custom_odds = d.custom_odds ? d.custom_odds : [];
      for (let i = 0; i < detailsData.custom_odds.length; i++) {
        detailsData.custom_odds[i][1] = parseFloat(
          detailsData.custom_odds[i][1]
        );
      }
      detailsData.custom_odds = JSON.stringify(detailsData.custom_odds);

      detailsData.max_liability = d.max_liability ? d.max_liability : [];
      for (let i = 0; i < detailsData.max_liability.length; i++) {
        detailsData.max_liability[i] = parseFloat(detailsData.max_liability[i]);
      }

      detailsData.custom_liability = d.custom_liability
        ? d.custom_liability
        : [];
      for (let i = 0; i < detailsData.custom_liability.length; i++) {
        detailsData.custom_liability[i][1] = parseFloat(
          detailsData.custom_liability[i][1]
        );
      }
      detailsData.custom_liability = JSON.stringify(
        detailsData.custom_liability
      );

      this.$store
        .dispatch(apiAction, detailsData)
        .then(resp => {
          let msg = "Lottery event updated";
          if (this.newId && resp.data.id) {
            this.updateId(resp.data.id);
            msg = "Lottery event created";
          }
          this.showSuccessNotification(msg);
        })
        .catch(err => {
          this.showErrorNotification(err);
        })
        .finally(() => {
          loader.close();
        });
    },
    populateData(r) {
      if (typeof r == "undefined") return;
      this.eid = typeof r.eid != "undefined" ? r.eid : "";

      this.k = r.k;
      this.n = r.n;
      this.r = r.r;
      this.m = r.m.toString();

      this.details.system_name = r.system_name;
      this.details.code = r.code;
      this.details.schedule_time = r.schedule_time;
      this.details.actual_time = r.actual_time;
      this.start1 = r.schedule_time ? this.mom_unix(r.schedule_time) : null;
      this.start2 = r.actual_time ? this.mom_unix(r.actual_time) : null;
      this.details.offered = r.offered;
      this.details.blocked = r.blocked;
      this.details.canceled = r.canceled;
      this.details.bet_block_offset = r.bet_block_offset;

      this.details.automatic_processing = r.automatic_processing;
      this.details.processed = r.processed;

      this.details.automatic_processing_max_amount =
        r.automatic_processing_max_amount;

      this.details.automatic_processing_max_ratio =
        r.automatic_processing_max_ratio;

      this.details.manual_processing_alert_emails = r.manual_processing_alert_emails
        ? r.manual_processing_alert_emails
        : [];
      this.details.odds = r.odds ? r.odds : [];
      this.details.custom_odds = r.custom_odds ? JSON.parse(r.custom_odds) : [];
      this.details.results_created = r.results_created
        ? this.mom_unix(r.results_created).format("DD-MM-YY, HH:mm")
        : null;
      this.details.results_modified = r.results_modified
        ? this.mom_unix(r.results_modified).format("DD-MM-YY, HH:mm")
        : null;
      this.details.results = r.results ? r.results : [];

      this.details.max_liability = r.max_liability ? r.max_liability : [];
      this.details.custom_liability = r.custom_liability
        ? JSON.parse(r.custom_liability)
        : [];

      try {
        this.process_results = JSON.parse(r.process_results);
      } catch (err) {
        // noop
        this.process_results = null;
      }
    },
    updateId(id) {
      this.eid = id;
      this.newId = false;
      this.$router.replace({ name: "Lottery Event", params: { id: id } });
    },
    showErrorNotification(err) {
      if (err.details && err.details.length) {
        this.$bvToast.toast(
          this.$t(err.details[0]),
          errorConf(this.$t("Error"))
        );
      } else {
        this.$bvToast.toast(this.$t(err.message), errorConf(this.$t("Error")));
      }
    },
    showSuccessNotification(msg) {
      this.$bvToast.toast(this.$t(msg), successConf(this.$t("Success")));
    },
    minutesToHours(total) {
      const minutes = total % 60;
      const hours = (total - minutes) / 60;
      return (
        (hours < 10 ? `0${hours}` : hours) +
        ":" +
        (minutes < 10 ? `0${minutes}` : minutes)
      );
    },
    hoursToMinutes(total) {
      if (!total) return 0;
      const parts = total.split(":", 2);
      const hours = parseInt(parts[0]) * 60;
      const minutes = parseInt(parts[1]);
      return hours + minutes;
    },
    updateScheduleTime(value) {
      this.details.schedule_time = new Date(value.startDate).getTime() * 1e6;
    },
    updateActualTime(value) {
      this.details.actual_time = new Date(value.startDate).getTime() * 1e6;
    },
    mom_unix(val) {
      return moment.unix(parseInt(val / 1e9));
    },
    mom(val) {
      return moment(val);
    },
    deleteResults() {
      const loader = this.$loading.open({
        container: this.$refs.respanel
      });
      const data = {
        eid: this.eid
      };
      this.$store
        .dispatch("lotteryEvent/clearResult", data)
        .then(resp => {
          let msg = "Results cleared";
          this.details.results_created = "";
          this.details.results_modified = "";
          this.details.results = [];
          this.showSuccessNotification(msg);
          this.loadData();
        })
        .catch(err => {
          this.showErrorNotification(err);
        })
        .finally(() => {
          loader.close();
        });
    },
    processResults() {
      const loader = this.$loading.open({
        container: this.$refs.respanel
      });
      const data = {
        eid: this.eid
      };
      this.$store
        .dispatch("lotteryEvent/process", data)
        .then(resp => {
          let msg = "Results processed";
          this.showSuccessNotification(msg);
          this.loadData();
        })
        .catch(err => {
          this.showErrorNotification(err);
        })
        .finally(() => {
          loader.close();
        });
    },
    addResults() {
      this.$store.dispatch("lotteryEvent/read", this.eid).then(resp => {
        this.populateData(resp.data);

        if (this.details.results.length == 0) {
          this.$bvModal.show("bv-modal-results");
        }
      });
    },
    onSaveResults() {
      const loader = this.$loading.open({
        container: this.$refs.l
      });

      const d = JSON.parse(JSON.stringify(this.resultSet));
      let apiAction = "lotteryEvent/setResult";

      const res = [];
      d.forEach(item => {
        res.push(parseInt(item));
      });

      const data = {
        numbers: res,
        eevent_id: this.eid,
        process_tickets: this.process,
        is_manual: true
      };

      this.$store
        .dispatch("lotteryEvent/setResult", data)
        .then(resp => {
          let msg = "Results saved";
          this.showSuccessNotification(msg);
          this.loadData();
          if (resp.data) {
            this.resultSetRes = resp.data;
          } else {
            this.$bvModal.hide("bv-modal-results");
          }
        })
        .catch(err => {
          this.showErrorNotification(err);
        })
        .finally(() => {
          loader.close();
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.border {
  position: relative;
}
.border {
  & /deep/ legend {
    position: relative;
    font-size: 0.8em;
    display: inline-block;
    cursor: text;
    color: #afb4d2;
    top: -0.1rem;
    pointer-events: none;
    width: auto;
    background: #fff;
    padding: 0 0.35rem;
  }
}
.text-black {
  color: #000;
}
</style>
